import { apiInstance } from '@/api/api.js'
import { ChatService } from '@/services/ChatService'
import { PUBNUB_TIMETOKEN_SHIFT, TIMETOKEN_TIMEOUT } from '@/helpers/constants'
import { debounce } from '@/helpers/debounce'
import { getDisplayName } from '@/helpers/getDisplayName'

const api = apiInstance()
let chatService = null
const CORRECT_STATUS = 200

export default {
  async getAllChatUsers({ commit }, params) {
    try {
      commit('setLoading', true)
      const response = await api.get('/contacts', { params })
      commit('setContacts', response.data.contacts)
      commit(
        'profilePreviewsStates/setProfilePreviews',
        response.data.profilePreviews,
        {
          root: true,
        },
      )
      commit('setLoading', false)
    } catch (error) {
      console.error(error)
      commit('setLoading', false)
    }
  },
  async getChatChannels({ commit, getters }) {
    try {
      const response = await api.get('/chats')
      if (response && response.status === 200) {
        commit('setLoading', false)
      }

      const newChannels = response.data.chats
      const newChannelsToSubscribe = newChannels
        .filter(channel => !getters.chatSubsribed.includes(channel.id))
        .map(channel => channel.id)

      if (chatService) {
        await chatService.subscribeNewChannels(newChannelsToSubscribe)
      }

      commit('setChannels', newChannels)
      commit('setPrivateChannel', response.data.prvChatId)
      commit(
        'profilePreviewsStates/setProfilePreviews',
        response.data.profilePreviews,
        {
          root: true,
        },
      )
    } catch (error) {
      console.error(error)
      commit('appStates/setSnackbarNotification', 'Please try again!', {
        root: true,
      })
    }
  },
  async createChat({ commit, dispatch, rootState, state }, chatData) {
    const { subject = '', contacts } = chatData
    const { profileId } = rootState.userStates

    try {
      const response = await api.post('/chats', {
        createdBy: profileId,
        name: subject,
        chatMembers: contacts,
        isDirectMessage: state.contactSingleSelection,
        isReadOnly: false,
      })

      const { data } = response
      if (chatService) {
        await chatService.subscribeNewChannels([data.id])
      }
      commit('addChannel', data)
      commit('setCurrentChannel', data.id)
      commit('profilePreviewsStates/setProfilePreviews', data.profilePreviews, {
        root: true,
      })
      dispatch('notifyMembers', data.chatMembers)

      return response
    } catch (error) {
      console.error(error)
      commit('appStates/setSnackbarNotification', 'Please try again!', {
        root: true,
      })
      return error
    }
  },
  setCurrentChannel({ dispatch, commit, state }, id) {
    if (!id) {
      commit('setCurrentChannel', '')
      return
    }

    if (!state.initializedChannels[id]) {
      const count =
        state.unreadMessagesObject[id] > state.historyMessagesCounter
          ? state.unreadMessagesObject[id]
          : state.historyMessagesCounter

      dispatch('getMoreMessages', { addTimetoken: false, count, channelId: id })
    }

    commit('setCurrentChannel', id)
  },
  async updateChat({ commit, dispatch, state }, updatedData) {
    const { subject, contacts } = updatedData
    const { currentChannelData } = state

    try {
      const oldChatMembers = [...(currentChannelData.chatMembers || [])]
      const response = await api.put(`/chats/${currentChannelData.id}`, {
        ...currentChannelData,
        name: subject,
        chatMembers: contacts,
      })

      const { data } = response

      const newChatMembers = [...(contacts || [])]

      // @todo we cannot rely on API response
      // commit('updateChannel', data)
      dispatch('getChatChannels')
      dispatch('notifyMembers', [...oldChatMembers, ...contacts])

      return response
    } catch (error) {
      console.error(error)
      commit('appStates/setSnackbarNotification', 'Please try again!', {
        root: true,
      })
      return error
    }
  },
  setContactSelectionOptionIsSingle({ getters, commit }, payload) {
    commit('setContactSelectionOption', payload)

    if (payload && getters.selectedContacts[1]) {
      // check if there is more than 1 contact
      commit('toggleContactsOnInit', [getters.selectedContacts[0]])
    }
  },
  notifyMembers({ state, getters, dispatch }, chatMembers = []) {
    const channels = chatMembers
      .map(member => {
        if (member.uuid === state.uuid) {
          return ''
        }

        return `${getters.channelPrefix}${member.uuid}`
      })
      .filter(el => el !== '')
      .filter((value, index, array) => array.indexOf(value) === index)

    channels.forEach(channelId => {
      dispatch('sendChatSignal', {
        channel: `${channelId}`,
        type: 'channel_created',
      })
    })
  },
  sendPushNotificationBulk({ state }, { channelIds = [], messageText = '' }) {
    api.post('/chats/notify/bulk', {
      message: messageText,
      chatIds: channelIds,
    })
  },
  sendPushNotification(
    { state, getters, dispatch },
    { channelId, messageText },
  ) {
    api.post(`/chats/${channelId}/notify`, { message: messageText })
  },
  async initChatService({ state, commit, dispatch, getters, rootState }, UUID) {
    if (!state.chatIsActive) {
      const isAdvisor = rootState.userStates.rolename === 'Advisor'
      commit('setUUID', UUID)
      chatService = new ChatService({
        uuid: UUID,
        channels: getters.chatSubsribed,
      })

      chatService.activateListeners({
        onMessage: msg => {
          if (msg?.message?.other?.message) {
            dispatch('userStates/showNotification', msg?.message?.other, {
              root: true,
            })
          }
          dispatch('onMessageCallback', msg)
        },
        onSignal: s => {
          if (s.channel === getters.personalChannel.id) {
            switch (s.message) {
              case 'channel_created':
                dispatch('getChatChannels')
                break
              case 'notification_created':
                if (!isAdvisor) {
                  dispatch(
                    'userStates/getNotificationsDebounced',
                    { isAdvisor: false },
                    {
                      root: true,
                    },
                  )
                } else {
                  dispatch(
                    'userStates/getNotificationsGroupedDebounced',
                    null,
                    {
                      root: true,
                    },
                  )
                }
                break
            }
          } else if (s.publisher === state.uuid) {
            return
          }

          switch (s.message) {
            case 'typing_on':
              commit('addActiveWriter', s)
              break
            case 'typing_off':
              commit('removeActiveWriter', s)
              break
          }
        },
        onMessageAction: ma => {
          const { data, event, channel } = ma
          commit('applyMessageActions', {
            payload: { data: [data], event },
            channel,
          })
        },
        onFile: file => {
          dispatch('onMessageCallback', file)
        },
        onStatus: status => {
          const supportedStatuses = [
            // Publish
            'PNNetworkIssuesCategory',

            // Subscription
            'PNNetworkDownCategory', // internet disconnected
            'PNNetworkUpCategory', // internet connected again
            'PNTimeoutCategory', // timeout
            'PNReconnectedCategory', // reconnected
            'PNAccessDeniedCategory', // PAM permission failure.
            'PNMalformedResponseCategory', // JSON parsing crashed.
            'PNDecryptionErrorCategory', // If using decryption strategies and the decryption fails
            'PNRequestMessageCountExceededCategory', // The SDK announces this error if requestMessageCountThreshold is set, and the number of messages received from PubNub (in-memory cache messages) exceeds the threshold.
            'PNUnknownCategory', // Returned when the subscriber gets a non-200 HTTP response code from the server.
          ]

          if (supportedStatuses.includes(status.category)) {
            console.log('onStatus', status, status.category)
          }

          if (status.category === 'PNNetworkUpCategory') {
            if (!isAdvisor) {
              dispatch(
                'userStates/getNotificationsDebounced',
                { isAdvisor: false },
                {
                  root: true,
                },
              )
            } else {
              dispatch('userStates/getNotificationsGroupedDebounced', null, {
                root: true,
              })
            }
          }
        },
      })

      dispatch('fetchInitMessages')
      commit('setChatIsActive', true)
      dispatch('getAllUnreadMessages')
    }
  },
  onMessageCallback({ state, commit, dispatch }, msg) {
    if (msg.publisher !== state.uuid) {
      if (state.currentChannel === msg.channel) {
        dispatch('markMessageAsRead', msg)
      } else {
        commit('channelNewUnreadedMessage', msg)
      }
    }
    commit('addMessage', msg)
  },
  unmountChatService() {
    chatService.deactivateListeners()
  },
  async fetchInitMessages({ state, getters, commit }) {
    const channelsMessagesWithActionsPromises = (
      getters.chatSubsribed || []
    ).map(channel => {
      return new Promise((resolve, reject) => {
        chatService.getMoreMessages(
          {
            channel,
            count: 1,
          },
          (status, response) => {
            if (!status || status.error) {
              reject(response)
            }
            resolve(response)
          },
        )
      })
    })

    const messagesWithActions = await Promise.all(
      channelsMessagesWithActionsPromises,
    )
    const channels = messagesWithActions.reduce(
      (r, c) => Object.assign(r, (c || {}).channels),
      {},
    )

    const initiatedEmptyChats = getters.channelsIdsWithoutPersonal.reduce(
      (acc, chat) => {
        if (!channels[chat]) {
          acc[chat] = true
        }

        return acc
      },
      {},
    )

    commit('fetchInitMessages', channels || {})
    Object.keys(channels).map(currentChannel => {
      commit('addHistoricalMessages', {
        messages: [...channels[currentChannel]],
        channel: currentChannel,
        initialized: true,
      })
    })
    commit('setInitializedChannels', initiatedEmptyChats)
  },
  async getMessageByTimetoken(
    { state, getters, commit },
    { channelId, timetoken },
  ) {
    const messageWithActions = await new Promise((resolve, reject) => {
      chatService.getMoreMessages(
        {
          channel: channelId,
          count: 1,
          date: `${parseInt(timetoken, 10) + TIMETOKEN_TIMEOUT}`,
        },
        (status, response) => {
          if (!status || status.error) {
            reject(response)
          }
          resolve(response)
        },
      )
    })
    let currentMessageObject = null
    let messageObject = null

    if (
      messageWithActions &&
      messageWithActions.channels &&
      messageWithActions.channels[channelId] &&
      messageWithActions.channels[channelId][0]
    ) {
      messageObject = messageWithActions.channels[channelId][0]
      currentMessageObject = messageObject

      if (messageObject.message && messageObject.message.file) {
        currentMessageObject = {
          ...messageObject,
          message: messageObject.message.message,
          file: messageObject.message.file,
        }
      }

      const { actions = {} } = messageObject
      Object.keys(actions).map(action => {
        const actionObj = actions[action] || {}
        let lastUpdate = {}

        switch (action) {
          case 'updated':
            Object.entries(actionObj).forEach(updateAction => {
              // updateAction[1]
              const foundNewerUpdate = updateAction[1].find(
                newUpdateAction =>
                  parseInt(newUpdateAction.actionTimetoken, 10) >
                  parseInt(lastUpdate.actionTimetoken || 0, 10),
              )
              if (foundNewerUpdate) {
                lastUpdate = {
                  ...foundNewerUpdate,
                  value: updateAction[0],
                }
              }
            })
            currentMessageObject.message = {
              ...currentMessageObject.message,
              text: lastUpdate.value,
              updated: true,
              updated_at: lastUpdate.messageTimetoken,
            }
            break
          case 'receipt':
            Object.keys(actionObj).map(type => {
              currentMessageObject.message[type] = true
            })
            break
          case 'deleted':
            Object.entries(actionObj).forEach(deletedAction => {
              const foundNewerUpdate = deletedAction[1].find(
                newDeletedAction =>
                  parseInt(newDeletedAction.actionTimetoken, 10) >
                  parseInt(lastUpdate.actionTimetoken || 0, 10),
              )
              if (foundNewerUpdate) {
                lastUpdate = {
                  ...foundNewerUpdate,
                  value: deletedAction[0],
                }
              }
            })
            currentMessageObject.message = {
              ...currentMessageObject.message,
              text: 'Message removed',
              deleted: true,
              deleted_at: lastUpdate.messageTimetoken,
            }
            break
          default:
            // reactions
            currentMessageObject.message.reactions = Object.keys(actionObj).map(
              reaction => ({
                type: reaction,
                users: (actionObj[reaction] || []).map(user => ({
                  user: user.uuid,
                  actionTimetoken: user.actionTimetoken,
                  messageTimetoken: messageObject.timetoken,
                })),
              }),
            )
        }
      })
    }

    return currentMessageObject
  },
  async bulkAddChatMessageFromMl(
    { state, commit, dispatch, getters, rootState },
    { messageText = '', channels = [], mediaLibraryFiles = null },
  ) {
    if (!mediaLibraryFiles || !channels || channels?.length === 0) {
      return
    }

    const successSentChannels = []
    const { user } = rootState.userStates
    const senderName =
      getDisplayName(
        user?.fullName,
        user?.firstName,
        user?.lastName,
        user?.nick,
      ) || ''

    let pushNotificationMessageText =
      senderName + (messageText ? `: ${messageText}` : ' sent you a message.')

    if (!senderName) {
      pushNotificationMessageText = 'Messages: You have a new message.'
    }

    let lastTimetoken = Date.parse(new Date().toISOString()) * Math.pow(10, 4)

    for (const channelData of channels) {
      const receiverUuid = channelData.uuid
      const mlFile = {
        senderUrl: mediaLibraryFiles.senderUrl,
        receiversUrl: {
          [receiverUuid]: mediaLibraryFiles.receiversUrl[receiverUuid],
        },
      }

      const sentMessageTimetokenString = await chatService.newAddChatMessage(
        channelData.chatId,
        {
          id: parseInt(Math.random() * 1000000, 10) + 1,
          messageText,
          messageLanguage: user.language,
          uuid: state.uuid,
          needAck: false,
          file: null,
          mediaLibraryFiles: mlFile,
          giphyFile: null,
          replyMessageTimetoken: null,
        },
      )

      if (sentMessageTimetokenString) {
        successSentChannels.push(channelData)

        const sentMessageTimetoken = parseInt(
          `${sentMessageTimetokenString?.timetoken || 0}`,
          10,
        )

        if (lastTimetoken < sentMessageTimetoken) {
          lastTimetoken = sentMessageTimetoken
        }
      }
    }

    const successSentChatIds = successSentChannels.map(el => el.chatId)

    // Update local timetokens
    dispatch('markMessagesAsRead', {
      updateTimetoken: false,
      chatIds: successSentChatIds,
    })
    dispatch('setChatMemberTimetokenBulk', {
      timetoken: lastTimetoken,
      chatIds: successSentChatIds,
    })
    dispatch('sendPushNotificationBulk', {
      messageText: pushNotificationMessageText,
      channelIds: successSentChatIds,
    })

    return successSentChannels
  },
  addChatMessage(
    { state, commit, dispatch, getters, rootState },
    {
      messageText = '',
      messageLanguage,
      channel = state.currentChannel,
      needAck = false,
      file,
      mediaLibraryFiles = null,
      giphyFile = null,
      __retry = false,
      replyMessageTimetoken = null,
    },
  ) {
    const { user } = rootState.userStates
    const senderName =
      getDisplayName(
        user?.fullName,
        user?.firstName,
        user?.lastName,
        user?.nick,
      ) || ''

    let pushNotificationMessageText =
      senderName + (messageText ? `: ${messageText}` : ' sent you a message.')

    if (!senderName) {
      pushNotificationMessageText = `${state.currentChannelData.name ||
        'Messages'}: You have a new message.`
    }

    const parsedDate = Date.parse(new Date().toISOString()) * Math.pow(10, 4)

    dispatch('addChatMessageCallbackDebounced', {
      timetoken: parsedDate,
      channelId: channel,
      messageText: '',
      time: 2000,
      sendPush: false,
    })

    chatService.addChatMessage(
      channel,
      {
        id: getters.currentMessageMaxId + 1,
        messageText,
        messageLanguage: messageLanguage || user.language,
        uuid: state.uuid,
        needAck,
        file,
        mediaLibraryFiles,
        giphyFile,
        replyMessageTimetoken,
      },
      (status, response) => {
        if (status.error) {
          if (
            mediaLibraryFiles &&
            !__retry &&
            status.errorType === 'MESSAGE_TO_BIG'
          ) {
            const newMediaLibraryFiles = {
              ...mediaLibraryFiles,
            }
            delete newMediaLibraryFiles.thumbnail

            dispatch('addChatMessage', {
              messageText,
              messageLanguage: messageLanguage || user.language,
              channel,
              needAck,
              file,
              mediaLibraryFiles: newMediaLibraryFiles,
              giphyFile,
              __retry: true,
              replyMessageTimetoken,
            })
          } else if (status.errorType === 'MESSAGE_TO_BIG') {
            commit(
              'appStates/setSnackbarNotification',
              'Message is too long.',
              { root: true },
            )
          } else {
            commit(
              'appStates/setSnackbarNotification',
              'Error occured. Please try again.',
              { root: true },
            )
          }
        } else if (response) {
          const { timetoken } = response

          dispatch('addChatMessageCallbackDebounced', {
            timetoken,
            channelId: channel,
            time: 0,
            messageText: pushNotificationMessageText,
          })
        }
      },
    )
  },
  addChatMessageCallbackDebounced(
    { dispatch },
    { timetoken, time, channelId, messageText, sendPush = true },
  ) {
    debounce(
      () =>
        dispatch('addChatMessageCallback', {
          timetoken,
          channelId,
          messageText,
          sendPush,
        }),
      time,
      'chatActions',
    )
  },
  addChatMessageCallback(
    { dispatch },
    { timetoken, channelId, messageText, sendPush = true },
  ) {
    if (sendPush) {
      dispatch('sendPushNotification', { channelId, messageText })
    }
    dispatch('setDebouncedChatMemberTimetoken', timetoken)
  },
  ackMessage({ dispatch }, message) {
    dispatch('addMessageAction', { message, type: 'receipt', value: 'ack' })
  },
  async removeMessage({ dispatch, commit }, message) {
    if (!message?.message?.mediaLibraryFiles) {
      dispatch('addMessageAction', {
        message,
        type: 'deleted',
        value: 'deleted',
      })
    } else {
      try {
        await api.post(
          `/ml/resources/deleteSent`,
          message?.message?.mediaLibraryFiles,
        )
        dispatch('addMessageAction', {
          message,
          type: 'deleted',
          value: 'deleted',
        })
      } catch (error) {
        commit(
          'appStates/setSnackbarNotification',
          'Error occured. Please try again.',
          { root: true },
        )
      }
    }
  },
  addMessageAction(
    { state, commit },
    { message, type = 'updated', value = '' },
  ) {
    const { timetoken } = message

    return chatService.addMessageAction(
      state.currentChannel,
      { timetoken, type, value },
      (status, response) => {
        if (status.error) {
          commit(
            'appStates/setSnackbarNotification',
            'Error occured. Please try again.',
            { root: true },
          )
        }
      },
    )
  },
  removeMessageAction({ state, commit }, action) {
    const { messageTimetoken, actionTimetoken } = action

    chatService.removeMessageAction(
      state.currentChannel,
      { messageTimetoken, actionTimetoken },
      (status, response) => {
        if (status.error) {
          commit(
            'appStates/setSnackbarNotification',
            'Error occured. Please try again.',
            { root: true },
          )
        }
      },
    )
  },
  getAllUnreadMessages({ state, getters, commit }) {
    if (getters.channelsIdsWithoutPersonal.length === 0) {
      // no channels so we don't need to call for unread messages
      return
    }

    const lastReadTimetokens = getters.channelsIdsWithoutPersonal.map(
      channelId => {
        const member =
          (state.initChatMembers[channelId] || []).find(
            member => member.uuid === state.uuid,
          ) || {}
        return (
          (member.timeToken &&
            `${member.timeToken + PUBNUB_TIMETOKEN_SHIFT}`) ||
          '1'
        )
      },
    )

    chatService.getAllUnreadMessages(
      {
        channels: getters.channelsIdsWithoutPersonal,
        lastReadTimetokens,
      },
      (_, response) => {
        if (response && response.channels) {
          commit('setAllUnreadMessages', response.channels)
        }
      },
    )
  },
  setDebouncedChatMemberTimetoken({ dispatch }, timetoken) {
    debounce(
      () => dispatch('setChatMemberTimetoken', timetoken),
      TIMETOKEN_TIMEOUT,
      'chatTimetoken',
    )
  },
  setChatMemberTimetokenBulk({ state }, { timetoken, chatIds = [] }) {
    if (!timetoken || !chatIds || chatIds?.length === 0) return

    const timeToken = parseInt(timetoken, 10)
    try {
      api.put(`/chats/chatMembers/bulk`, { timeToken, chatIds })
    } catch (error) {
      console.error(error)
    }
  },
  setChatMemberTimetoken({ state }, timetoken) {
    const timeToken = parseInt(timetoken, 10)
    try {
      api.put(`/chats/${state.currentChannel}/chatMembers`, { timeToken })
    } catch (error) {
      console.error(error)
    }
  },
  markMessageAsRead({ dispatch }, message) {
    dispatch('addMessageAction', { message, type: 'receipt', value: 'is_read' })
    dispatch('setDebouncedChatMemberTimetoken', message.timetoken)
  },
  markMessagesAsRead({ state, getters, commit, dispatch }, params = null) {
    const chatIds = params?.chatIds || [state.currentChannel]
    const updateTimetoken = params?.updateTimetoken !== false

    let readActions = []

    for (const channelId of chatIds) {
      if (
        state.unreadMessagesObject[channelId] !== 0 ||
        getters.unreadedMessages[channelId].length !== 0
      ) {
        const currentMessages = state.messages[channelId] || []
        const lastMessage = currentMessages[currentMessages.length - 1] || {}

        readActions = getters.unreadedMessages[channelId].map(message => {
          return dispatch('addMessageAction', {
            message,
            type: 'receipt',
            value: 'is_read',
          })
        })

        commit('setChannelUnreadMessages', {
          channel: channelId,
          currentValue: 0,
        })

        if (lastMessage.timetoken && updateTimetoken) {
          dispatch('setChatMemberTimetoken', lastMessage.timetoken)
        }
      }
    }

    return Promise.all(readActions)
  },
  sendChatSignal(_, { type, channel }) {
    if (!channel || !type) {
      return
    }

    chatService.sendChatSignal(channel, type)
  },
  async toggleChannelIsPinned({ commit, state }, channel) {
    const pinned = (
      channel.chatMembers.find(member => member.uuid === state.uuid) || {}
    ).isChatPinned

    try {
      const response = await api.put(`/chats/${channel.id}/chatMembers`, {
        pinned: !pinned,
      })
      commit('setChatMembers', {
        chatMembers: response.data.chatMembers,
        id: channel.id,
      })
    } catch (error) {
      console.error(error)
      commit('setLoading', false)
    }
  },
  async setChatArchive({ state, commit, dispatch }, channel) {
    const isHidden = (
      channel.chatMembers.find(member => member.uuid === state.uuid) || {}
    ).isHidden
    commit('setLoading', true)
    try {
      const response = await api.put(`/chats/${channel.id}/chatMembers`, {
        isHidden: !isHidden,
      })
      dispatch('getChatChannels')
      if (response.status === 200) {
        commit(
          'appStates/setSnackbarNotification',
          `${
            !isHidden
              ? `${channel?.name} chat moved to archive`
              : `Archived chat moved to active messages`
          }`,
          {
            root: true,
          },
        )
      }
    } catch (error) {
      commit('setLoading', false)
      dispatch(
        'appStates/setErrorMessage',
        `Error occurred while marked ${channel?.name} chat as archived. Please try again.`,
        { root: true },
      )
    }
  },

  // files
  getFileUrl({ state }, { name, id }) {
    return chatService.getFileUrl({ channel: state.currentChannel, name, id })
  },
  async downloadFile(
    { state, commit },
    { file, channel = state.currentChannel },
  ) {
    try {
      const blob = await chatService.downloadFile({ file, channel })
      const url = window.URL.createObjectURL(await blob.toFile())
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = file.name || 'empty'
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 100)
    } catch {
      commit(
        'appStates/setSnackbarNotification',
        'Error occured. Please try again.',
        { root: true },
      )
    }
  },

  // history
  getMoreMessages(
    { state, commit, dispatch },
    {
      count,
      addTimetoken = true,
      timetoken = '',
      channelId,
      previousMessages = [],
    },
  ) {
    const oldestMessagesTimetoken = addTimetoken
      ? timetoken || state.messages[state.currentChannel][0].timetoken
      : ''
    const currentChannel = channelId || state.currentChannel

    commit('setLoading', true)

    chatService.getMoreMessages(
      {
        channel: currentChannel,
        ...(addTimetoken ? { date: oldestMessagesTimetoken } : {}),
        count: count || state.historyMessagesCounter,
      },
      (_, response) => {
        if (response && response.more) {
          return dispatch('getMoreMessages', {
            count: response.more.max,
            timetoken: response.more.start,
            channelId,
            addTimetoken: true,
            previousMessages: [
              ...response.channels[currentChannel],
              ...previousMessages,
            ],
          })
        }

        if (response && response.channels) {
          commit('addHistoricalMessages', {
            messages: [
              ...response.channels[currentChannel],
              ...previousMessages,
            ],
            channel: currentChannel,
            initialized: !!state.initializedChannels[currentChannel],
          })

          if (
            response.channels[currentChannel].length <
            state.historyMessagesCounter
          ) {
            commit('setChannelWithFullHistory', currentChannel)
          }

          dispatch('markMessagesAsRead')
        } else {
          commit('setChannelWithFullHistory', currentChannel)
        }

        commit('setLoading', false)
      },
    )
  },

  copyMessage({ state, commit }, { messageText = '' }) {
    navigator.clipboard.writeText(`${messageText}`)
    commit('appStates/setSnackbarNotification', 'Message copied!', {
      root: true,
    })
  },
  async sendAiQuestion({ state, commit, dispatch }, { profileId, question }) {
    try {
      const response = await api.post(`/sami`, {
        targetProfileId: profileId,
        question,
      })
      return response?.data
    } catch (error) {
      commit(
        'appStates/setSnackbarNotification',
        'Error occured. Please try again.',
        { root: true },
      )
      return false
    }
  },
  async sendAiAnswer({ state, commit, dispatch }, { finalAnswer, answerId }) {
    try {
      const response = await api.put(`/sami/${answerId}`, {
        finalAnswer,
      })
      return response?.data
    } catch (error) {
      return false
    }
  },
  async createChatBulk(
    { commit, dispatch, rootState, state },
    { participants = [] },
  ) {
    if (!participants || participants.length === 0) return

    const body = participants.map(el => ({ participantId: el.id }))

    try {
      const response = await api.post('/chats/bulk', body)

      dispatch('getChatChannels')
      dispatch('notifyMembers', participants)

      return response
    } catch (error) {
      console.error(error)
      commit('appStates/setSnackbarNotification', 'Please try again!', {
        root: true,
      })
      return error
    }
  },
  async getChatMessageDraft(_ctx, { chatId }) {
    try {
      const res = await api.get(`/chats/${chatId}/message-draft`)
      return res?.status === 204 ? null : res.data
    } catch (error) {
      return null
    }
  },
  async saveChatMessageDraft(_ctx, { chatId, message }) {
    try {
      await api.put(`/chats/${chatId}/message-draft`, { message })
    } catch (error) {
      /* silent */
    }
  },
  async deleteChatMessageDraft(_ctx, { chatId }) {
    try {
      await api.delete(`/chats/${chatId}/message-draft`)
    } catch (error) {
      /* silent */
    }
  },
}
